import { ErrorMessage, Field, Form, Formik } from 'formik'
import React, { ChangeEvent, ReactNode, useState } from 'react'
import * as Yup from 'yup'
import { Msg, useMsg } from '~/common/localizationUtils'
import { SadStates } from '~/components/SadStates'
import { customReportsApi, useReportSettings } from '~/custom_reports/api'
import { STANDARD_REPORTS_PATH, REPORT_FILES_PATH } from '~/custom_reports/constants'
import { ReportQuery } from '~/custom_reports/types'
import {
  Box,
  Button,
  ButtonGroupActions,
  ButtonProps,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  IconButton,
  Input,
  Link,
  MenuItem,
  ModalDialog,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  useErrorModal,
  useInfoModal,
  useSuccessToast
} from '~/design_system'
import { Download } from '~/icons'
import { Loader } from '~/reports/table/Loader'
import VndlySelect from '../../components/VndlySelect'
import { useWaitAndDownload, useWaitAndDownloadReportFile } from './helpers'

const STANDARD_REPORT_OPTIONS = [
  { label: 'Excel', value: 'excel' },
  { label: 'CSV', value: 'csv' }
]

const NON_STANDARD_REPORT_OPTIONS = [
  { label: 'Excel', value: 'xls' },
  { label: 'CSV', value: 'csv' },
  { label: 'Pipe Separated', value: 'txt' },
  { label: 'Tab Separated', value: 'tsv' }
]

type ExportFormModalProps = {
  name?: string
  onExport: (values: { export_format: string }) => void
  setIsExporting: (state: boolean) => void
  setShowExportModal: (state: boolean) => void
  isStandardReport: boolean | undefined
  isExporting: boolean
  show: boolean
}

// TODO: Instead of making query an option, we should refactor to make the query a requirement
// and remove the fetch query that would condiitionally fetch query
type ReportExportProps = {
  id?: number | string | null
  report_type: string
  query?: ReportQuery | null
  queryFinder?: () => Promise<ReportQuery | null | undefined>
  name?: string
  queryParams?: any
  isSpecialReport?: boolean
  isStandardReport?: boolean
  isReportSchema?: boolean
  isExportAsLink?: boolean
  isExportAsMenuItem?: boolean
  asIcon?: boolean
  exportButtonText?: string
  exportButtonLoadingText?: string
  children?: ReactNode
  isDisabled?: boolean
  size?: ButtonProps['size']

  // TODO: Remove me once the old builder is deprecated. Used to not conflict icon colours
  iconColor_TEMP?: string
  hideIcon?: boolean
}

const ExportFormModal = ({
  name,
  onExport,
  setIsExporting,
  setShowExportModal,
  isStandardReport = false,
  isExporting = false,
  show = false
}: ExportFormModalProps) => {
  const onHide = () => setShowExportModal(false)
  const onSubmit = (values: any) => {
    setIsExporting(true)
    onExport(values)
  }
  const msg = useMsg()
  const { settings = { exporting: {} }, isLoading } = useReportSettings()
  const initialValues = {
    name,
    export_format: isStandardReport ? 'excel' : 'xls',
    export_file_extension: 'xlsx',
    isFetchingDefaultSettings: isLoading,
    ...settings,
    ...settings.exporting
  }
  const exportOptions = isStandardReport ? STANDARD_REPORT_OPTIONS : NON_STANDARD_REPORT_OPTIONS
  const exportFileValidation = Yup.object({
    name: Yup.string()
      .min(2, msg('common.ui.error.too_short'))
      .max(70, msg('common.ui.error.too_long'))
      .required(msg('common.ui.error.required')),
    export_file_extension: Yup.string()
      .min(1, msg('common.ui.error.too_short'))
      .max(15, msg('common.ui.error.too_long'))
      .required(msg('common.ui.error.required'))
  })

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={exportFileValidation}
      enableReinitialize>
      {({ handleSubmit, setFieldValue, values, errors, touched }) => (
        <ModalDialog size="xl" isOpen={show} onClose={onHide}>
          <Form
            onSubmit={handleSubmit}
            onKeyDown={e => {
              // we stop the keydown from inputs to propagate upward to the modal
              // https://github.com/chakra-ui/chakra-ui/issues/3579
              e.stopPropagation()
            }}>
            <fieldset disabled={initialValues.isFetchingDefaultSettings}>
              <ModalDialogHeader>
                <Msg id="report.export.modal.title" />
              </ModalDialogHeader>
              <ModalDialogBody>
                <SadStates states={[{ when: initialValues.isFetchingDefaultSettings, render: () => <Loader /> }]}>
                  <Grid templateColumns="repeat(12, 1fr)" columnGap={4} pb={4}>
                    <GridItem colSpan={12} w="100%" mb={4}>
                      <FormControl isRequired isInvalid={!!(errors.name && touched.name)}>
                        <Flex>
                          <FormLabel w="25%" mt="auto" mb="auto" mr={0}>
                            <Msg id="common.ui.export_name" />
                          </FormLabel>
                          <Field
                            style={{ height: '40px', paddingLeft: '4px', width: '75%' }}
                            name="name"
                            component={Input}
                            value={values.name}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('name', e.target.value)}
                            isDisabled={isStandardReport}
                          />
                        </Flex>
                        <FormErrorMessage>
                          <ErrorMessage name="name" />
                        </FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={8} w="100%">
                      <FormControl>
                        <FormLabel>
                          <Msg id="common.ui.export_format" />
                        </FormLabel>
                        <VndlySelect
                          width="100%"
                          inline
                          name=""
                          value={values.export_format}
                          options={exportOptions}
                          onChange={(e: { value: string }) => {
                            setFieldValue('export_format', e.value)
                            setFieldValue('export_file_extension', e.value === 'xls' ? 'xlsx' : e.value)
                          }}
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={4} h="100%">
                      <FormControl
                        isRequired
                        isInvalid={!!(errors.export_file_extension && touched.export_file_extension)}>
                        <FormLabel>
                          <Msg id="common.ui.file_extension" />
                        </FormLabel>
                        <Field
                          style={{ height: '40px', paddingLeft: '4px' }}
                          name="export_file_extension"
                          component={Input}
                          value={values.export_file_extension === 'excel' ? 'xlsx' : values.export_file_extension}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setFieldValue('export_file_extension', e.target.value)
                          }
                          isDisabled={values.export_format === 'xls' || isStandardReport}
                        />
                        <FormErrorMessage>
                          <ErrorMessage name="export_file_extension" />
                        </FormErrorMessage>
                      </FormControl>
                    </GridItem>
                  </Grid>
                  {!isStandardReport && (
                    <>
                      <Box mb={2}>
                        <Flex justifyContent="flex-start" alignItems="center">
                          <Box width="md">
                            <Field
                              as={Checkbox}
                              name="include_report_heading"
                              size="lg"
                              isChecked={values.include_report_heading}
                              onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValue('include_report_heading', evt.target.checked)
                              }}>
                              <Msg id="report.export.modal.include_report_creation_detail_option" />
                            </Field>
                          </Box>
                        </Flex>
                      </Box>
                      <Box mb={2}>
                        <Flex justifyContent="flex-start" alignItems="center">
                          <Box width="md">
                            <Field
                              as={Checkbox}
                              name="include_column_headers"
                              size="lg"
                              isChecked={values.include_column_headers}
                              onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValue('include_column_headers', evt.target.checked)
                              }}>
                              <Msg id="report.export.modal.include_column_headers_option" />
                            </Field>
                          </Box>
                        </Flex>
                      </Box>
                    </>
                  )}
                  <Link href={REPORT_FILES_PATH} verticalAlign="baseline" mr={1} isExternal pr={1}>
                    <Msg id="common.ui.click_here" />
                  </Link>
                  <Msg id="report.export.modal.to_view_all_export_jobs" />
                </SadStates>
              </ModalDialogBody>
              {!initialValues.isFetchingDefaultSettings && (
                <ModalDialogFooter>
                  <ButtonGroupActions>
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={isExporting}
                      loadingText={`${msg('common.ui.exporting')}`}>
                      <Msg id="common.ui.export" />
                    </Button>
                    <Button onClick={onHide}>
                      {isExporting ? <Msg id="common.ui.background" /> : <Msg id="common.ui.cancel" />}
                    </Button>
                  </ButtonGroupActions>
                </ModalDialogFooter>
              )}
            </fieldset>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  )
}

type ExportButtonProps = {
  exportButtonText?: string
  exportButtonLoadingText?: string
  asIcon?: boolean
  onClick: (event: React.MouseEvent<Element, MouseEvent>) => Promise<void>
  isDisabled?: boolean
  isExporting: boolean
  hideIcon?: boolean
}

const ExportButton = (props: ExportButtonProps) => {
  const msg = useMsg()
  const {
    exportButtonText,
    exportButtonLoadingText,
    onClick,
    isExporting,
    asIcon,
    isDisabled,
    hideIcon,
    ...otherProps
  } = props

  const buttonProps = {
    isLoading: isExporting,
    isDisabled: isExporting || isDisabled,
    onClick
  }
  const loadingText = exportButtonLoadingText ?? msg('common.ui.exporting')
  const buttonText = exportButtonText ?? msg('common.ui.export')
  return asIcon ? (
    <IconButton
      aria-label={msg('common.ui.download', { count: 1 })}
      icon={<Download />}
      showTooltip
      data-testid="export_report_icon_button"
      isRound
      {...buttonProps}
      {...otherProps}
    />
  ) : (
    <Button
      loadingText={loadingText}
      {...(!hideIcon && { leftIcon: <Download /> })}
      data-testid="export_report_button"
      {...buttonProps}
      {...otherProps}>
      {buttonText}
    </Button>
  )
}

type exportProps = {
  export_format?: string
}

export type taskProps = {
  result?: {
    message?: boolean | React.ReactElement<any> | number | string | Iterable<React.ReactNode> | React.ReactPortal
  }
}

// TODO: Remove all of standard reports logic when we remove standard reports
/**
 * Report Export Button
 * @param id (required) id of the report
 * @param type (required) report type
 * @param name report name
 * @param query report query
 * @param queryFinder an async function that returns query of the report
 * @param queryParams query params like { description }
 * @param noMenu if true, the export button will not have a dropdown menu
 * @param isSpecialReport true if the report is derived from ReportTableWrapper component
 * @param isStandardReport true if the report is standard report
 * @param isReportSchema true if we are calling this from report schema
 * @param isExportAsLink true if this is an "Export As" link
 */
export const ReportExport = ({
  id,
  report_type,
  query,
  queryFinder,
  name,
  queryParams,
  isSpecialReport,
  isStandardReport = false,
  isReportSchema,
  isExportAsLink = false,
  isExportAsMenuItem = false,
  exportButtonText,
  children,
  asIcon,
  iconColor_TEMP = 'var(--vndly-color-container-icon)',
  ...props
}: ReportExportProps) => {
  const msg = useMsg()
  const { showModal: showExportInfoModal } = useInfoModal()
  const { showModal: showErrorModal } = useErrorModal()
  const successToast = useSuccessToast()
  const [isExporting, setIsExporting] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  let finalQuery = query

  const fetchQuery = async (event: { stopPropagation: () => void; preventDefault: () => void }, openModal = false) => {
    event.stopPropagation()
    event.preventDefault()
    setIsExporting(true)
    if (!finalQuery) {
      /*
      eslint-disable-next-line react-hooks/immutability --

      ⚠️HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: Reassigns `finalQuery` during async flow.
      Possible fix approach:
        const finalQueryRef = useRef(query)
        if (!finalQueryRef.current) finalQueryRef.current = await queryFinder()
      */
      finalQuery = queryFinder ? await queryFinder() : null
    }
    setIsExporting(!openModal)
    setShowExportModal(openModal)
  }

  const waitAndDownloadTask = useWaitAndDownload()
  const waitAndDownloadReportFile = useWaitAndDownloadReportFile()
  const waitAndDownload = !isReportSchema && !isSpecialReport ? waitAndDownloadReportFile : waitAndDownloadTask
  const onExport = async (values: exportProps = {}) => {
    if (isStandardReport) {
      window.open(`${STANDARD_REPORTS_PATH}/?id=${id}&export_${values.export_format}=True`, '_blank')
      setIsExporting(false)
      setShowExportModal(false)
    } else {
      try {
        let task_id
        if (isReportSchema) {
          ;({ task_id = null } = await customReportsApi.exportReportSchema(queryParams))
        } else if (isSpecialReport) {
          ;({ task_id = null } = await customReportsApi.exportSpecialReport(id, {
            ...values,
            ...queryParams,
            report_name: name
          }))
        } else {
          ;({ task_id = null } = await customReportsApi.exportReport(
            {
              id,
              report_type,
              query: finalQuery
            },
            { ...values, ...queryParams, report_name: name }
          ))
        }
        waitAndDownload(task_id, {
          onTaskSuccess: (task: taskProps) => {
            setIsExporting(false)
            setShowExportModal(false)
            successToast(task?.result?.message)
          },
          onTaskFail: (task: taskProps) => {
            setIsExporting(false)
            showErrorModal({
              title: msg('report.export.modal.failed_export.title'),
              message: task?.result?.message
            })
          },
          onRetryLimitReached: (_task: taskProps) => {
            setIsExporting(false)
            showExportInfoModal({
              title: msg('report.export.modal.title'),
              message: msg('report.export.modal.message_1'),
              linkButtonText: <Msg id="report.export.modal.report_history" />,
              linkButtonHref: REPORT_FILES_PATH
            })
          },
          showAlertModal: false
        })
      } catch {
        showErrorModal({
          title: msg('report.export.modal.failed_export.title'),
          message: msg('common.contact_technical_support_team')
        })
        setIsExporting(false)
      }
    }
  }

  return (
    <>
      {/* TODO(TomC): investigate why report won't auto download if the Sadstate has children :sad-face: */}
      {/* @ts-expect-error */}
      <SadStates
        states={[
          {
            when: isStandardReport,
            render: () => (
              <ExportButton
                isExporting={isExporting}
                data-testid="export_standard_report_button"
                onClick={event => fetchQuery(event, true)}
                isDisabled={props.isDisabled}
                asIcon={asIcon}
              />
            )
          },
          {
            when: isExportAsMenuItem,
            render: () => (
              <MenuItem
                data-testid="export-menu-item"
                icon={<Download color={iconColor_TEMP} />}
                onClick={event => fetchQuery(event, true)}>
                {children}
              </MenuItem>
            )
          },
          {
            when: isExportAsLink,
            render: () => (
              <Box data-testid="export-as-link" onClick={event => fetchQuery(event, true)}>
                {children}
              </Box>
            )
          },
          {
            when: !isExportAsLink,
            render: () => (
              <ExportButton
                onClick={event => fetchQuery(event).then(() => onExport())}
                exportButtonText={exportButtonText}
                isExporting={isExporting}
                isDisabled={props.isDisabled}
                {...props}
                asIcon={asIcon}
              />
            )
          }
        ]}
      />
      <ExportFormModal
        {...{ name, onExport, setIsExporting, setShowExportModal, isStandardReport, isExporting }}
        show={showExportModal}
      />
    </>
  )
}
